<template>
  <div style="display: flex" class="otp-input-container">
    <!--    To turn off autocomplete when otp-input is password-->
    <input
      v-if="inputType === 'password'"
      autocomplete="off"
      name="hidden"
      type="text"
      style="display: none"
    />
    <SingleOtpInput
      v-for="(_, i) in numInputs"
      :key="i"
      :focus="activeInput === i"
      :value="otp[i]"
      :separator="separator"
      :input-type="inputType"
      :inputmode="inputmode"
      :input-classes="inputClasses"
      :conditionalClass="conditionalClass?.[i]"
      :is-last-child="i === numInputs - 1"
      :is-first-child="i === 0"
      :should-auto-focus="shouldAutoFocus"
      :placeholder="placeholder?.[i]"
      :is-disabled="isDisabled"
      :total-inputs="numInputs"
      @on-change="handleOnChange"
      @on-keydown="handleOnKeyDown($event, i)"
      @on-paste="handleOnPaste"
      @on-focus="handleOnFocus(i)"
      @on-blur="handleOnBlur"
    />
  </div>
</template>

<script>
import { ref, watch } from "vue";
import SingleOtpInput from "./SingleOtpInput.vue";

// Constants for keyCode
const BACKSPACE = 8;
const LEFT_ARROW = 37;
const RIGHT_ARROW = 39;
const DELETE = 46;

export default {
  name: "OtpInput",
  components: {
    SingleOtpInput,
  },
  props: {
    value: {
      type: String,
      required: false,
    },
    numInputs: {
      type: Number,
      default: 4,
    },
    separator: {
      type: String,
      default: "",
    },
    inputClasses: {
      type: [String, Array],
      default: "",
    },
    conditionalClass: {
      type: Array,
      default: () => [],
    },
    inputType: {
      type: String,
      default: "text",
      validator(value) {
        return ["number", "tel", "letter-numeric", "password"].includes(value);
      },
    },
    inputmode: {
      type: String,
      default: "text",
    },
    shouldAutoFocus: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: Array,
      default: () => [],
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    shouldFocusOrder: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeInput: 0,
      otp: Array(this.numInputs).fill(""),
      oldOtp: [],
    };
  },
  watch: {
    value(val) {
      if (val.length === this.numInputs || this.otp.length === 0) {
        this.otp = val.split("");
      }
    },
  },
  methods: {
    handleOnFocus(index) {
      this.activeInput = index;
      this.$emit("on-handle-focus");
    },
    handleOnBlur() {
      this.activeInput = -1;
      this.$emit("on-handle-blur");
    },
    checkFilledAllInputs() {
      if (this.otp.join("").length === this.numInputs) {
        this.$emit("update:value", this.otp.join(""));
        this.$emit("on-complete", this.otp.join(""));
      }
    },
    focusInput(input) {
      this.activeInput = Math.max(Math.min(this.numInputs - 1, input), 0);
    },
    focusNextInput() {
      this.focusInput(this.activeInput + 1);
    },
    focusPrevInput() {
      this.focusInput(this.activeInput - 1);
    },
    changeCodeAtFocus(value) {
      this.oldOtp = [...this.otp];
      this.otp[this.activeInput] = value.toString();

      if (this.oldOtp.join("") !== this.otp.join("")) {
        this.$emit("update:value", this.otp.join(""));
        this.$emit("on-change", this.otp.join(""));
        this.checkFilledAllInputs();
      }
    },
    handleOnPaste(event) {
      event.preventDefault();
      const pastedData = event.clipboardData
        .getData("text/plain")
        .slice(0, this.numInputs - this.activeInput)
        .split("");

      if (this.inputType === "number" && !pastedData.join("").match(/^\d+$/)) {
        return "Invalid pasted data";
      }

      if (
        this.inputType === "letter-numeric" &&
        !pastedData.join("").match(/^\w+$/)
      ) {
        return "Invalid pasted data";
      }

      const currentCharsInOtp = this.otp.slice(0, this.activeInput);
      const combinedWithPastedData = currentCharsInOtp.concat(pastedData);

      combinedWithPastedData.slice(0, this.numInputs).forEach((value, i) => {
        this.otp[i] = value;
      });

      this.focusInput(combinedWithPastedData.slice(0, this.numInputs).length);
      this.checkFilledAllInputs();
    },
    handleMultipleInputInSingleInput(value) {
      const pastedData = value
        .toString()
        .slice(0, this.numInputs - this.activeInput)
        .split("");

      if (this.inputType === "number" && !pastedData.join("").match(/^\d+$/)) {
        return "Invalid pasted data";
      }

      if (
        this.inputType === "letter-numeric" &&
        !pastedData.join("").match(/^\w+$/)
      ) {
        return "Invalid pasted data";
      }

      const currentCharsInOtp = this.otp.slice(0, this.activeInput);
      const combinedWithPastedData = currentCharsInOtp.concat(pastedData);

      combinedWithPastedData.slice(0, this.numInputs).forEach((value, i) => {
        this.otp[i] = value;
      });

      this.focusInput(combinedWithPastedData.slice(0, this.numInputs).length);
      this.checkFilledAllInputs();
    },
    handleOnChange(value) {
      if (value.toString().length > 1) {
        this.handleMultipleInputInSingleInput(value);
      } else if (value === "") {
        this.changeCodeAtFocus(value);
      } else {
        this.changeCodeAtFocus(value);
        this.focusNextInput();
      }
    },
    clearInput() {
      if (this.otp.length > 0) {
        this.$emit("update:value", "");
        this.$emit("on-change", "");
      }
      this.otp = [];
      this.activeInput = 0;
    },
    fillInput(value) {
      const fill = value.split("");
      if (fill.length === this.numInputs) {
        this.otp = fill;
        this.$emit("update:value", this.otp.join(""));
        this.$emit("on-complete", this.otp.join(""));
      }
    },
    handleOnKeyDown(event, index) {
      switch (event.keyCode) {
        case BACKSPACE:
          event.preventDefault();
          if (this.otp[this.activeInput]) {
            this.changeCodeAtFocus("");
          } else {
            this.focusPrevInput();
          }
          break;
        case DELETE:
          event.preventDefault();
          this.changeCodeAtFocus("");
          break;
        case LEFT_ARROW:
          event.preventDefault();
          this.focusPrevInput();
          break;
        case RIGHT_ARROW:
          event.preventDefault();
          this.focusNextInput();
          break;
        default:
          this.focusOrder(index);
          break;
      }
    },
    focusOrder(currentIndex) {
      if (this.shouldFocusOrder) {
        setTimeout(() => {
          const len = this.otp.join("").length;
          if (currentIndex - len >= 0) {
            this.activeInput = len;
            this.otp[currentIndex] = "";
          }
        }, 100);
      }
    },
  },
};
</script>
